import React from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';

import { useStores } from '../context/context';
import { Helmet } from 'react-helmet';

// System pages
import Home from '../pages/Home/Home';
import NotFound from '../pages/NotFound';
import AuthPage from '../pages/Auth/AuthPage';
import MyOrders from '../pages/MyOrders/MyOrders';
import SignUpPage from '../pages/Auth/SignUpPage';
import DevicePage from '../pages/Devices/DevicePage';
import TicketPage from '../pages/Tickets/TicketPage';
import PaymentPage from '../pages/Payments/PaymentPage';
import TrackingPage from '../pages/Tickets/TrackingPage';
import SystemPage from '../pages/Other/SystemPage';
import FeedbackPage from '../pages/MyOrders/FeedbackPage';
import CustomerPage from '../pages/Customers/CustomerPage';
import TechnicianPage from '../pages/Users/TechnicianPage';
import DeviceListPage from '../pages/Devices/DeviceListPage';
import Logout from '../pages/Auth/Logout';

// Help pages
import GoogleHelpPage from '../pages/Help/GoogleHelpPage';

export const SITE_DESCRIPTION =
  'On demand device repairs. Your device, fixed and returned - without leaving home.';

// Flat route config for v6
const ROUTES = [
  {
    path: '/',
    key: 'ROOT',
    component: Home,
    title: 'Repatch - Choose a Device',
  },
  {
    path: '/login',
    key: 'LOGIN',
    component: AuthPage,
    title: 'Repatch - Login',
  },
  {
    path: '/logout',
    key: 'LOGOUT',
    component: Logout,
    title: 'Repatch - Logout',
  },
  {
    path: '/signup',
    key: 'SIGNUP',
    component: SignUpPage,
    title: 'Repatch - Sign Up',
  },
  {
    path: '/reset',
    key: 'RESET',
    component: Home,
  },
  {
    path: '/:deviceType-repair/:manufacturer/:device',
    key: 'DEVICEPAGE',
    component: DevicePage,
  },
  {
    path: '/:manufacturer',
    key: 'MANUFACTURERONLY',
    component: DeviceListPage,
  },
  {
    path: '/:deviceType-repair/:manufacturer?',
    key: 'DEVICELIST',
    component: DeviceListPage,
  },
  {
    path: '/ticket/auth',
    key: 'TICKETAUTH',
    component: AuthPage,
  },
  {
    path: '/ticket/pay',
    key: 'TICKETPAY',
    component: PaymentPage,
    private: true,
    title: 'Repatch - Payment',
  },
  {
    path: '/ticket/:ticketID',
    key: 'TICKET',
    component: TicketPage,
    private: true,
    title: 'Repatch - Your Quotes',
  },
  {
    path: '/ticket/',
    key: 'TICKETROOT',
    component: TicketPage,
    private: true,
    title: 'Repatch - Your Quotes',
  },
  {
    path: '/track/:ticketID',
    key: 'TRACK',
    component: TrackingPage,
    private: true,
    title: 'Repatch - Order Tracking',
  },
  {
    path: '/profile',
    key: 'CUSTOMERHOME',
    component: CustomerPage,
    private: true,
    title: 'Repatch - My Settings',
  },
  {
    path: '/orders',
    key: 'MYORDERS',
    component: MyOrders,
    private: true,
    title: 'Repatch - My Orders',
  },
  {
    path: '/system',
    key: 'SYSTEM',
    component: SystemPage,
  },
  {
    path: '/feedback/:ticketID',
    key: 'FEEDBACK',
    component: FeedbackPage,
    private: true,
  },
  {
    path: '/technician/:userID',
    key: 'TECHNICIAN',
    component: TechnicianPage,
  },
  {
    path: '/help/how-to-find-google-model-number',
    key: 'GOOGLEHELP',
    component: GoogleHelpPage,
    title: 'Repatch - Google Model Number Help',
  },
];

function PrivateRoute({ children }) {
  const { authStore } = useStores();
  const location = useLocation();
  if (!authStore.isAuthenticated) {
    let path = location.pathname;
    if (window.location.search) {
      path += window.location.search;
    }
    return <Navigate to="/login" state={{ path }} replace />;
  }
  return children;
}

function RouteWithHelmet({ element, title }) {
  return (
    <>
      <Helmet>
        {title && <title>{title}</title>}
        <meta name="description" content={SITE_DESCRIPTION} />
      </Helmet>
      {element}
    </>
  );
}

export function RenderRoutes() {
  return (
    <Routes>
      {ROUTES.map((route) => {
        let element = <route.component />;
        if (route.private) {
          element = <PrivateRoute>{element}</PrivateRoute>;
        }
        element = <RouteWithHelmet element={element} title={route.title} />;
        return (
          <Route
            key={route.key}
            path={route.path}
            element={element}
          />
        );
      })}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default ROUTES;
